/**
 * Product reviews as the API serves them and the browser sends them. A
 * review waits for a moderator before anyone but its author sees it.
 */
import { z } from "zod";
import { StoredText } from "./catalog.js";
export const ReviewId = {
    schema: z.uuid().brand(),
};
export const ReviewStatus = {
    NAMES: ["pending", "approved", "rejected"],
    schema: z.enum(["pending", "approved", "rejected"]),
    label: {
        pending: "Waiting for approval",
        approved: "Published",
        rejected: "Rejected",
    },
};
export const ReviewSort = {
    NAMES: ["helpful", "newest", "highest", "lowest"],
    schema: z.enum(["helpful", "newest", "highest", "lowest"]),
    label: {
        helpful: "Most helpful",
        newest: "Newest",
        highest: "Highest rating",
        lowest: "Lowest rating",
    },
};
/** A text field a person may leave empty, which then counts as absent. */
export const OptionalText = {
    of: (max, tooLong) => z
        .string()
        .trim()
        .refine((text) => StoredText.lengthOf(text) <= max, tooLong)
        .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE)
        .transform((text) => (text.length === 0 ? undefined : text))
        .optional(),
};
export const ReviewInput = {
    schema: z.strictObject({
        rating: z
            .number({ error: "Choose a rating." })
            .int("Choose a whole number of stars.")
            .min(1, "Choose 1 to 5 stars.")
            .max(5, "Choose 1 to 5 stars."),
        /** The name the review is signed with, which keeps the account's own name and email private. */
        authorName: z
            .string({ error: "Enter the name to sign with." })
            .trim()
            // Counted as the database's check counts them.
            .refine((text) => StoredText.lengthOf(text) >= 2, "Use at least 2 characters.")
            .refine((text) => StoredText.lengthOf(text) <= 40, "Use at most 40 characters.")
            .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
        title: OptionalText.of(120, "Use at most 120 characters."),
        body: OptionalText.of(2000, "Use at most 2,000 characters."),
    }),
};
export const RejectReviewInput = {
    schema: z.strictObject({
        reason: z
            .string({ error: "Say why, for the author." })
            .trim()
            .min(3, "Say why, for the author.")
            .max(300, "Use at most 300 characters.")
            .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    }),
};
export const ReviewQueryParams = {
    schema: z.object({
        sort: ReviewSort.schema.default("helpful"),
        page: z.coerce.number().int().min(1).max(10_000).default(1),
    }),
};
//# sourceMappingURL=reviews.js.map